import React, { useEffect, useMemo, useRef, useState } from "react";
import { Activity, BarChart3, RefreshCw, TrendingDown, TrendingUp } from "lucide-react";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";
const STORAGE_KEY = "nexovate_indicator_candles_v1";
const BIG_MONEY_STORAGE_KEY = "nexovate_big_money_punch_tape_v1";
const MAX_CANDLES = 180;
const DEFAULT_BIG_MONEY_CR = 5;

const INDICES = [
  { key: "nifty", label: "NIFTY", exchange: "NSE" },
  { key: "banknifty", label: "BANKNIFTY", exchange: "NSE" },
  { key: "sensex", label: "SENSEX", exchange: "BSE" },
  { key: "bankex", label: "BANKEX", exchange: "BSE" },
];

const TIMEFRAMES = [
  { value: 1, label: "1 Min" },
  { value: 3, label: "3 Min" },
  { value: 5, label: "5 Min" },
  { value: 15, label: "15 Min" },
  { value: 30, label: "30 Min" },
];

const INTERNAL_TABS = [
  { key: "live", label: "Live Indicators" },
  { key: "bigMoney", label: "Big Money Flow" },
];

const OPTION_LOT_SIZE = {
  nifty: 75,
  banknifty: 35,
  sensex: 20,
  bankex: 30,
};

const C = {
  bg: "#030706",
  panel: "#080b08",
  card: "#090d0b",
  line: "rgba(247,201,72,0.25)",
  gold: "#f7c948",
  dim: "#8a6d25",
  green: "#22c55e",
  red: "#ef4444",
  blue: "#38bdf8",
  purple: "#a78bfa",
  text: "#f8e38a",
};

function readStore() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveStore(store) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function readBigMoneyStore() {
  try {
    return JSON.parse(localStorage.getItem(BIG_MONEY_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveBigMoneyStore(store) {
  localStorage.setItem(BIG_MONEY_STORAGE_KEY, JSON.stringify(store));
}

function bucketTime(timestamp, minutes) {
  const date = new Date(timestamp);
  date.setSeconds(0, 0);
  const bucket = Math.floor(date.getMinutes() / minutes) * minutes;
  date.setMinutes(bucket);
  return date.toISOString();
}

function ema(values, period) {
  const result = Array(values.length).fill(null);
  if (values.length < period) return result;

  const multiplier = 2 / (period + 1);
  let previous = values.slice(0, period).reduce((sum, value) => sum + value, 0) / period;
  result[period - 1] = previous;

  for (let i = period; i < values.length; i += 1) {
    previous = (values[i] - previous) * multiplier + previous;
    result[i] = previous;
  }

  return result;
}

function rma(values, period) {
  const result = Array(values.length).fill(null);
  if (values.length < period) return result;

  let previous = values.slice(0, period).reduce((sum, value) => sum + value, 0) / period;
  result[period - 1] = previous;

  for (let i = period; i < values.length; i += 1) {
    previous = ((previous * (period - 1)) + values[i]) / period;
    result[i] = previous;
  }

  return result;
}

function calculateIndicators(candles) {
  const closes = candles.map(candle => Number(candle.close || 0));
  const ema20 = ema(closes, 20);
  const ema50 = ema(closes, 50);

  const trueRanges = [0];
  const plusDm = [0];
  const minusDm = [0];

  for (let i = 1; i < candles.length; i += 1) {
    const current = candles[i];
    const previous = candles[i - 1];
    const upMove = current.high - previous.high;
    const downMove = previous.low - current.low;

    trueRanges.push(Math.max(
      current.high - current.low,
      Math.abs(current.high - previous.close),
      Math.abs(current.low - previous.close)
    ));

    plusDm.push(upMove > downMove && upMove > 0 ? upMove : 0);
    minusDm.push(downMove > upMove && downMove > 0 ? downMove : 0);
  }

  const atr14 = rma(trueRanges, 14);
  const smoothPlusDm = rma(plusDm, 14);
  const smoothMinusDm = rma(minusDm, 14);

  const plusDi = candles.map((_, i) => atr14[i] ? (100 * smoothPlusDm[i]) / atr14[i] : null);
  const minusDi = candles.map((_, i) => atr14[i] ? (100 * smoothMinusDm[i]) / atr14[i] : null);
  const dx = candles.map((_, i) => {
    if (plusDi[i] == null || minusDi[i] == null) return 0;
    const total = plusDi[i] + minusDi[i];
    return total ? (100 * Math.abs(plusDi[i] - minusDi[i])) / total : 0;
  });
  const adx = rma(dx, 14);

  return { ema20, ema50, plusDi, minusDi, adx };
}

function latestValue(values) {
  for (let i = values.length - 1; i >= 0; i -= 1) {
    if (values[i] != null && Number.isFinite(values[i])) return values[i];
  }
  return null;
}

function fmt(value, digits = 2) {
  return value == null || !Number.isFinite(value) ? "-" : Number(value).toFixed(digits);
}

function compact(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "-";
  if (Math.abs(number) >= 10000000) return `${(number / 10000000).toFixed(2)}Cr`;
  if (Math.abs(number) >= 100000) return `${(number / 100000).toFixed(2)}L`;
  if (Math.abs(number) >= 1000) return `${(number / 1000).toFixed(1)}K`;
  return number.toFixed(0);
}

function moneyCr(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? `${(number / 10000000).toFixed(2)}Cr` : "-";
}

function timeToMinutes(value) {
  const [hour, minute] = String(value || "00:00").split(":").map(Number);
  return hour * 60 + minute;
}

function minuteLabel(totalMinutes) {
  return `${String(Math.floor(totalMinutes / 60)).padStart(2, "0")}:${String(totalMinutes % 60).padStart(2, "0")}`;
}

function buildBigMoneyIntervalRows(snapshots, intervalMinutes) {
  const start = timeToMinutes("09:15");
  const end = timeToMinutes("15:30");
  const byMinute = new Map();

  (snapshots || []).forEach(snapshot => {
    if (snapshot?.minute) byMinute.set(snapshot.minute, snapshot);
  });

  return Array.from({ length: Math.ceil((end - start) / intervalMinutes) }, (_, position) => {
    const intervalStart = start + position * intervalMinutes;
    const intervalEnd = Math.min(intervalStart + intervalMinutes, end);
    const records = [];

    for (let minute = intervalStart + 1; minute <= intervalEnd; minute += 1) {
      const record = byMinute.get(minuteLabel(minute));
      if (record) records.push(record);
    }

    const latest = records[records.length - 1] || null;
    const rows = latest?.rows || [];
    const ceRows = rows.filter(row => row.optionSide === "CE");
    const peRows = rows.filter(row => row.optionSide === "PE");

    return {
      serial: position + 1,
      label: `${minuteLabel(intervalStart)} - ${minuteLabel(intervalEnd)}`,
      records,
      rows,
      ceRows,
      peRows,
      totalAmount: rows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
      totalActive: rows.reduce((sum, row) => sum + Number(row.remainingAmount || 0), 0),
    };
  });
}

function activeAmountDetail(row) {
  const delta = Number(row.lastDeltaAmount || 0);
  if (!delta) return "no fresh change";

  return delta > 0
    ? `+${moneyCr(delta)} added`
    : `-${moneyCr(Math.abs(delta))} left`;
}

function asMap(rows) {
  return new Map((rows || []).map(row => [row.key, row]));
}

function optionRowsFromSnapshot(snapshot) {
  const rows = [];

  Object.entries(snapshot?.optionChains || {}).forEach(([chainKey, chain]) => {
    const lotSize = OPTION_LOT_SIZE[chainKey] || 1;
    ["calls", "puts"].forEach(bucket => {
      const optionSide = bucket === "calls" ? "CE" : "PE";

      (chain?.[bucket] || []).forEach(row => {
        const strike = Number(row.strike || 0);
        const ltp = Number(row.ltp || 0);
        const volume = Number(row.volume || 0);
        if (!strike || !ltp) return;

        rows.push({
          key: `${chainKey}:${optionSide}:${strike}`,
          instrumentKey: `${chainKey}:${optionSide}:${strike}`,
          market: chain.source || "NSE/BSE",
          index: chain.index || chainKey.toUpperCase(),
          label: `${chain.index || chainKey.toUpperCase()} ${strike} ${optionSide}`,
          type: "option",
          optionSide,
          strike,
          ltp,
          volume,
          oi: Number(row.oi || 0),
          oiChange: Number(row.oiChange || 0),
          iv: Number(row.iv || 0),
          lotSize,
        });
      });
    });
  });

  return rows;
}

function stockRowsFromSnapshot(snapshot) {
  return (snapshot?.stocks || []).map(row => ({
    key: `stock:${row.symbol}`,
    instrumentKey: `stock:${row.symbol}`,
    market: row.source || "NSE",
    index: row.index || "",
    label: row.symbol,
    name: row.name || row.symbol,
    type: "stock",
    price: Number(row.price || 0),
    volume: Number(row.volume || 0),
    value: Number(row.value || 0),
    change: Number(row.change || 0),
    pChange: Number(row.pChange || 0),
  })).filter(row => row.label && row.price);
}

function buildLargeOptionEvents(snapshot, previousRows, thresholdRupees) {
  const previous = asMap(previousRows);
  const now = optionRowsFromSnapshot(snapshot);

  return now
    .map(row => {
      const prev = previous.get(row.key);
      const volumeDelta = Math.max(0, row.volume - Number(prev?.volume || 0));
      const quantity = volumeDelta;
      const amount = quantity * row.ltp;
      const freshPremium = amount;
      const ltpChange = prev ? row.ltp - Number(prev.ltp || 0) : 0;

      if (amount < thresholdRupees) return null;

      return {
        id: `${row.key}:${snapshot.updatedAt || Date.now()}`,
        instrumentKey: row.instrumentKey,
        type: "option",
        optionSide: row.optionSide,
        strike: row.strike,
        market: row.market,
        label: row.label,
        side: ltpChange >= 0 ? "BUY" : "SELL",
        status: row.oiChange >= 0 ? "HOLDING / OI ADDED" : "EXIT / OI UNWIND",
        quantity,
        lots: volumeDelta,
        amount,
        freshPremium,
        remainingQuantity: quantity,
        remainingAmount: amount,
        lastDeltaAmount: amount,
        price: row.ltp,
        ltpChange,
        oi: row.oi,
        oiChange: row.oiChange,
        iv: row.iv,
        time: snapshot.updatedAt || new Date().toISOString(),
      };
    })
    .filter(Boolean);
}

function buildCurrentLargeOptionRows(snapshot, thresholdRupees) {
  return optionRowsFromSnapshot(snapshot)
    .map(row => {
      const quantity = row.volume;
      const amount = quantity * row.ltp;
      if (amount < thresholdRupees) return null;

      return {
        id: `current:${row.key}`,
        instrumentKey: row.instrumentKey,
        type: "option",
        optionSide: row.optionSide,
        strike: row.strike,
        market: row.market,
        label: row.label,
        side: row.oiChange >= 0 ? "BUY" : "SELL",
        status: "CURRENT STRIKE FLOW",
        quantity,
        lots: row.volume,
        amount,
        freshPremium: 0,
        remainingQuantity: quantity,
        remainingAmount: amount,
        lastDeltaAmount: 0,
        price: row.ltp,
        ltpChange: 0,
        oi: row.oi,
        oiChange: row.oiChange,
        iv: row.iv,
        time: snapshot.updatedAt || new Date().toISOString(),
      };
    })
    .filter(Boolean)
    .sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0))
    .slice(0, 80);
}

function buildLargeStockEvents(snapshot, previousRows, thresholdRupees) {
  const previous = asMap(previousRows);
  const now = stockRowsFromSnapshot(snapshot);

  return now
    .map(row => {
      const prev = previous.get(row.key);
      const volumeDelta = Math.max(0, row.volume - Number(prev?.volume || 0));
      const amount = volumeDelta * row.price;
      const priceChange = prev ? row.price - Number(prev.price || 0) : row.change;

      if (amount < thresholdRupees) return null;

      return {
        id: `${row.key}:${snapshot.updatedAt || Date.now()}`,
        instrumentKey: row.instrumentKey,
        type: "stock",
        market: row.market,
        label: row.label,
        name: row.name,
        side: priceChange >= 0 ? "BUY" : "SELL",
        status: "ACTIVE FLOW",
        quantity: volumeDelta,
        amount,
        remainingQuantity: volumeDelta,
        remainingAmount: amount,
        lastDeltaAmount: amount,
        price: row.price,
        priceChange,
        pChange: row.pChange,
        time: snapshot.updatedAt || new Date().toISOString(),
      };
    })
    .filter(Boolean);
}

function buildCurrentLargeStockRows(snapshot, thresholdRupees) {
  return stockRowsFromSnapshot(snapshot)
    .map(row => {
      const amount = row.volume * row.price;
      if (amount < thresholdRupees) return null;

      return {
        id: `current:${row.key}`,
        instrumentKey: row.instrumentKey,
        type: "stock",
        market: row.market,
        label: row.label,
        name: row.name,
        side: row.change >= 0 ? "BUY" : "SELL",
        status: "CURRENT STOCK FLOW",
        quantity: row.volume,
        amount,
        remainingQuantity: row.volume,
        remainingAmount: amount,
        lastDeltaAmount: 0,
        price: row.price,
        priceChange: row.change,
        pChange: row.pChange,
        time: snapshot.updatedAt || new Date().toISOString(),
      };
    })
    .filter(Boolean)
    .sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0))
    .slice(0, 80);
}

function bigMoneyHistoryRowsFromSnapshot(snapshot) {
  return optionRowsFromSnapshot(snapshot)
    .map(row => {
      const quantity = row.volume;
      const amount = quantity * row.ltp;

      return {
        id: `history:${row.key}`,
        instrumentKey: row.instrumentKey,
        type: "option",
        optionSide: row.optionSide,
        strike: row.strike,
        market: row.market,
        label: row.label,
        side: row.oiChange >= 0 ? "BUY" : "SELL",
        status: row.oiChange >= 0 ? "HOLDING / OI ADDED" : "EXIT / OI UNWIND",
        amount,
        remainingAmount: amount,
        quantity,
        lots: row.volume,
        remainingQuantity: quantity,
        price: row.ltp,
        ltpChange: 0,
        oi: row.oi,
        oiChange: row.oiChange,
        iv: row.iv,
        time: snapshot.updatedAt || new Date().toISOString(),
      };
    })
    .filter(row => row.price > 0)
    .sort((a, b) => {
      if (a.optionSide !== b.optionSide) return a.optionSide.localeCompare(b.optionSide);
      return Number(b.amount || 0) - Number(a.amount || 0);
    });
}

function mergeActivePunches(activeRows, newRows) {
  const active = (activeRows || [])
    .filter(row => Number(row.remainingAmount || 0) > 0)
    .map(row => ({ ...row }));

  newRows.forEach(newRow => {
    let remainingAmount = Number(newRow.amount || 0);
    let remainingQuantity = Number(newRow.quantity || 0);
    const sameSideRow = active.find(row =>
      row.instrumentKey === newRow.instrumentKey && row.side === newRow.side
    );

    if (sameSideRow) {
      sameSideRow.remainingAmount = Number(sameSideRow.remainingAmount || 0) + remainingAmount;
      sameSideRow.remainingQuantity = Number(sameSideRow.remainingQuantity || 0) + remainingQuantity;
      sameSideRow.amount = Number(sameSideRow.amount || 0) + remainingAmount;
      sameSideRow.quantity = Number(sameSideRow.quantity || 0) + remainingQuantity;
      sameSideRow.lastDeltaAmount = remainingAmount;
      sameSideRow.status = newRow.status || sameSideRow.status || "ACTIVE FLOW";
      sameSideRow.price = newRow.price;
      sameSideRow.time = newRow.time;
      return;
    }

    active.forEach(row => {
      if (remainingAmount <= 0) return;
      if (row.instrumentKey !== newRow.instrumentKey || row.side === newRow.side) return;

      const exitAmount = Math.min(Number(row.remainingAmount || 0), remainingAmount);
      const exitRatio = remainingAmount ? exitAmount / remainingAmount : 0;
      row.remainingAmount = Math.max(0, Number(row.remainingAmount || 0) - exitAmount);
      row.remainingQuantity = Math.max(0, Number(row.remainingQuantity || 0) - remainingQuantity * exitRatio);
      row.lastDeltaAmount = -exitAmount;
      row.status = row.remainingAmount > 0 ? "PART EXIT" : "EXITED";
      remainingAmount -= exitAmount;
      remainingQuantity = Math.max(0, remainingQuantity - remainingQuantity * exitRatio);
    });

    if (remainingAmount > 0) {
      active.push({
        ...newRow,
        remainingAmount,
        remainingQuantity,
        lastDeltaAmount: remainingAmount,
        status: newRow.status || "ACTIVE FLOW",
      });
    }
  });

  return active
    .filter(row => Number(row.remainingAmount || 0) > 0)
    .sort((a, b) => Number(b.remainingAmount || 0) - Number(a.remainingAmount || 0))
    .slice(0, 80);
}

function updateBigMoneyPunchTape(snapshot, previousStore, thresholdCr) {
  const thresholdRupees = Number(thresholdCr || DEFAULT_BIG_MONEY_CR) * 10000000;
  const previousOptionRows = previousStore?.previousOptionRows || [];
  const previousStockRows = previousStore?.previousStockRows || [];
  const nextOptionRows = optionRowsFromSnapshot(snapshot);
  const nextStockRows = stockRowsFromSnapshot(snapshot);
  const optionEvents = buildLargeOptionEvents(snapshot, previousOptionRows, thresholdRupees);
  const stockEvents = buildLargeStockEvents(snapshot, previousStockRows, thresholdRupees);
  const mergedOptions = mergeActivePunches(previousStore?.options || [], optionEvents);
  const mergedStocks = mergeActivePunches(previousStore?.stocks || [], stockEvents);

  return {
    thresholdCr,
    updatedAt: snapshot.updatedAt || new Date().toISOString(),
    previousOptionRows: nextOptionRows,
    previousStockRows: nextStockRows,
    options: mergedOptions.length ? mergedOptions : buildCurrentLargeOptionRows(snapshot, thresholdRupees),
    stocks: mergedStocks.length ? mergedStocks : buildCurrentLargeStockRows(snapshot, thresholdRupees),
    lastOptionEvents: optionEvents.slice(0, 30),
    lastStockEvents: stockEvents.slice(0, 30),
  };
}

function getSignal({ ema20, ema50, plusDi, minusDi, adx }) {
  const e20 = latestValue(ema20);
  const e50 = latestValue(ema50);
  const pdi = latestValue(plusDi);
  const mdi = latestValue(minusDi);
  const ax = latestValue(adx);

  if (e20 == null || e50 == null || pdi == null || mdi == null || ax == null) {
    return {
      label: "BUILDING DATA",
      color: C.dim,
      detail: "Need more candles for full EMA50 and ADX reading.",
    };
  }

  if (ax < 20) {
    return {
      label: "RANGE / WEAK TREND",
      color: C.gold,
      detail: `ADX ${fmt(ax)} is below 20, avoid aggressive trend entries.`,
    };
  }

  if (e20 > e50 && pdi > mdi && ax >= 25) {
    return {
      label: ax >= 30 ? "STRONG BULLISH TREND" : "BULLISH TREND",
      color: C.green,
      detail: "EMA20 is above EMA50, DI+ is above DI-, ADX confirms trend strength.",
    };
  }

  if (e20 < e50 && mdi > pdi && ax >= 25) {
    return {
      label: ax >= 30 ? "STRONG BEARISH TREND" : "BEARISH TREND",
      color: C.red,
      detail: "EMA20 is below EMA50, DI- is above DI+, ADX confirms trend strength.",
    };
  }

  return {
    label: "MIXED / WAIT",
    color: C.blue,
    detail: "EMA direction and DI direction are not fully aligned.",
  };
}

function valueToY(value, min, max, height) {
  if (max === min) return height / 2;
  return height - ((value - min) / (max - min)) * height;
}

function linePath(values, min, max, width, height) {
  const points = values
    .map((value, index) => {
      if (value == null || !Number.isFinite(value)) return null;
      const x = values.length <= 1 ? 0 : (index / (values.length - 1)) * width;
      const y = valueToY(value, min, max, height);
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .filter(Boolean);

  return points.length ? `M ${points.join(" L ")}` : "";
}

function PriceChart({ candles, indicators }) {
  const width = 900;
  const height = 280;
  const recentCandles = candles.slice(-80);
  const offset = candles.length - recentCandles.length;
  const closes = recentCandles.map(candle => candle.close);
  const e20 = indicators.ema20.slice(offset);
  const e50 = indicators.ema50.slice(offset);
  const allValues = [...closes, ...e20, ...e50].filter(value => value != null && Number.isFinite(value));
  const min = allValues.length ? Math.min(...allValues) : 0;
  const max = allValues.length ? Math.max(...allValues) : 1;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height: 300 }}>
      <rect x="0" y="0" width={width} height={height} fill="#050806" stroke={C.line} />
      {[0.25, 0.5, 0.75].map(ratio => (
        <line key={ratio} x1="0" x2={width} y1={height * ratio} y2={height * ratio} stroke="rgba(247,201,72,0.12)" />
      ))}
      <path d={linePath(closes, min, max, width, height)} fill="none" stroke={C.text} strokeWidth="2.4" />
      <path d={linePath(e20, min, max, width, height)} fill="none" stroke={C.green} strokeWidth="2" />
      <path d={linePath(e50, min, max, width, height)} fill="none" stroke={C.red} strokeWidth="2" />
      <text x="14" y="24" fill={C.text} fontSize="13">PRICE</text>
      <text x="14" y="46" fill={C.green} fontSize="12">EMA 20</text>
      <text x="86" y="46" fill={C.red} fontSize="12">EMA 50</text>
      <text x={width - 130} y="24" fill={C.dim} fontSize="12">Last {recentCandles.length} candles</text>
    </svg>
  );
}

function AdxChart({ indicators }) {
  const width = 900;
  const height = 180;
  const adx = indicators.adx.slice(-80);
  const plusDi = indicators.plusDi.slice(-80);
  const minusDi = indicators.minusDi.slice(-80);
  const allValues = [...adx, ...plusDi, ...minusDi].filter(value => value != null && Number.isFinite(value));
  const max = Math.max(50, ...allValues);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height: 200 }}>
      <rect x="0" y="0" width={width} height={height} fill="#050806" stroke={C.line} />
      {[20, 25, 30].map(level => (
        <line
          key={level}
          x1="0"
          x2={width}
          y1={valueToY(level, 0, max, height)}
          y2={valueToY(level, 0, max, height)}
          stroke={level === 25 ? "rgba(247,201,72,0.42)" : "rgba(247,201,72,0.18)"}
          strokeDasharray="6 5"
        />
      ))}
      <path d={linePath(adx, 0, max, width, height)} fill="none" stroke={C.gold} strokeWidth="2.4" />
      <path d={linePath(plusDi, 0, max, width, height)} fill="none" stroke={C.green} strokeWidth="2" />
      <path d={linePath(minusDi, 0, max, width, height)} fill="none" stroke={C.red} strokeWidth="2" />
      <text x="14" y="24" fill={C.gold} fontSize="13">ADX</text>
      <text x="68" y="24" fill={C.green} fontSize="12">DI+</text>
      <text x="112" y="24" fill={C.red} fontSize="12">DI-</text>
      <text x={width - 110} y={valueToY(25, 0, max, height) - 6} fill={C.gold} fontSize="11">ADX 25</text>
    </svg>
  );
}

function Metric({ label, value, color = C.text }) {
  return (
    <div className="rounded border px-4 py-3" style={{ background: C.card, borderColor: C.line }}>
      <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: C.dim }}>{label}</div>
      <div className="mt-2 text-xl font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

function BigMoneyTable({
    title,
    rows,
    type,
    sortField,
    sortDirection,
    setSortField,
    setSortDirection
}) {

    const handleSort = (field) => {

        if (sortField === field) {
            setSortDirection(
                sortDirection === "asc"
                    ? "desc"
                    : "asc"
            );
        } else {
            setSortField(field);
            setSortDirection("desc");
        }

    };

  return (
    <div
      className="rounded border overflow-auto"
      style={{
        background: C.panel,
        borderColor: C.line,
      }}
    >
      <div
        className="px-4 py-3 text-lg font-bold"
        style={{
          background: "#078574",
          color: C.gold,
        }}
      >
        {title}
      </div>

      <table
        className="w-full border-collapse text-sm"
        style={{ minWidth: 1200 }}
      >
        <thead
            style={{
                background: "#0d0d0d",
                color: C.gold,
                position: "sticky",
                top: 0,
                zIndex: 5
            }}
        >
          <tr>
            <th className="border px-3 py-2">#</th>
            <th className="border px-3 py-2">Time</th>
            <th
                className="border px-3 py-2"
                style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 6,
                    background: "#0d0d0d"
                }}
            >
                Name
            </th>
            <th
                className="border px-3 py-2 cursor-pointer"
                onClick={() => handleSort("price")}
            >
                Rate
                {sortField === "price"
                    ? (sortDirection === "asc" ? " ▲" : " ▼")
                    : ""}
            </th>
            <th
                className="border px-3 py-2 cursor-pointer"
                onClick={() => handleSort("remainingQuantity")}
            >
                Qty
                {sortField === "remainingQuantity"
                    ? (sortDirection === "asc" ? " ▲" : " ▼")
                    : ""}
            </th>
            <th
                className="border px-3 py-2 cursor-pointer"
                onClick={() => handleSort("remainingAmount")}
            >
                Value (Cr)
                {sortField === "remainingAmount"
                    ? (sortDirection === "asc" ? " ▲" : " ▼")
                    : ""}
            </th>
            <th>Fresh Premium</th>
            <th>Status</th>
          </tr>
        </thead>

        <tbody>
          {rows
              .map((row, index) => {

                  const buy = row.side === "BUY";

                  return (
                      <tr
                          key={row.id}
                          style={{
                              background: buy
                                  ? "rgba(0,140,60,.25)"
                                  : "rgba(140,0,0,.25)",
                              transition: "all .2s ease",
                              cursor: "pointer"
                          }}
                      >
                <td className="border px-3 py-2">
                  {index + 1}
                </td>

                <td className="border px-3 py-2">
                  {new Date(row.time).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: false
                  })}
                </td>

                <td
                    className="border px-3 py-2 font-bold"
                    style={{
                        position: "sticky",
                        left: 0,
                        background: buy
                            ? "rgba(0,140,60,.95)"
                            : "rgba(140,0,0,.95)",
                        zIndex: 2,
                        color: "#fff"
                    }}
                >
                    {row.label}
                </td>

                <td className="border px-3 py-2">
                  {fmt(row.price)}
                </td>

                <td className="border px-3 py-2">
                  {compact(row.remainingQuantity)}
                </td>

                <td
                    className="border px-3 py-2 font-bold"
                    style={{
                        color:
                            Number(row.remainingAmount) >= 500000000
                                ? "#00ff66"
                                : C.gold
                    }}
                >
                    {moneyCr(row.remainingAmount)}
                </td>

                <td
                  className="border px-3 py-2 font-bold"
                  style={{
                    color:
                      Number(row.freshPremium || 0) > 0
                        ? "#00ff66"
                        : "#888"
                  }}
                >
                  {moneyCr(row.freshPremium || 0)}
                </td>

                <td className="border px-3 py-2">
                  {row.status}
                </td>

              </tr>
            );

          })}

        </tbody>
      </table>
    </div>
  );
}

function SavedPunchColumn({ title, rows }) {
  const totalActive = rows.reduce((sum, row) => sum + Number(row.remainingAmount || 0), 0);
  const netAmount = rows.reduce((sum, row) => {
    const amount = Number(row.remainingAmount || row.amount || 0);
    return row.side === "SELL" ? sum - amount : sum + amount;
  }, 0);
  const netText = netAmount > 0
    ? `+${moneyCr(netAmount)} added`
    : netAmount < 0
      ? `-${moneyCr(Math.abs(netAmount))} left`
      : "no fresh change";

  return (
    <div className="rounded border p-5" style={{ background: C.panel, borderColor: C.line }}>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-xl font-bold" style={{ color: C.gold }}>{title}</h3>
        <div className="text-xs font-bold" style={{ color: C.dim }}>{rows.length} rows</div>
      </div>
      <div className="mb-4 grid gap-3 md:grid-cols-2">
        <div className="rounded border px-4 py-3" style={{ background: C.card, borderColor: C.line }}>
          <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: C.dim }}>Total Still Active</div>
          <div className="mt-1 text-lg font-bold" style={{ color: C.gold }}>{moneyCr(totalActive)}</div>
        </div>
        <div className="rounded border px-4 py-3" style={{ background: C.card, borderColor: C.line }}>
          <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: C.dim }}>Net Added / Left</div>
          <div className="mt-1 text-lg font-bold" style={{ color: netAmount < 0 ? C.red : netAmount > 0 ? C.green : C.dim }}>
            {netText}
          </div>
        </div>
      </div>
      <div className="overflow-auto rounded border" style={{ borderColor: C.line }}>
        <table className="w-full min-w-[1180px] border-collapse text-sm">
          <thead style={{ background: "#078574", color: C.gold }}>
            <tr>
              {["Strike", "Side", "Status", "Amount", "Still Active", "Quantity", "Holding Qty", "Price", "Lots", "OI", "OI Chg", "IV"].map(head => (
                <th key={head} className="border px-3 py-3 text-left" style={{ borderColor: C.line }}>{head}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const color = row.side === "BUY" ? C.green : C.red;
              return (
                <tr key={`${row.id}-${row.optionSide}`} style={{ background: C.card, color }}>
                  <td className="border px-3 py-3 font-bold" style={{ borderColor: C.line }}>{row.label}</td>
                  <td className="border px-3 py-3 font-bold" style={{ borderColor: C.line }}>{row.side}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line }}>{row.status || "-"}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line, color: C.gold }}>{moneyCr(row.amount)}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line }}>{moneyCr(row.remainingAmount)}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line, color: C.text }}>{compact(row.quantity)}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line, color: C.text }}>{compact(row.remainingQuantity)}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line, color: C.text }}>{fmt(row.price)}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line, color: C.text }}>{compact(row.lots)}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line, color: C.text }}>{compact(row.oi)}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line, color: C.text }}>{compact(row.oiChange)}</td>
                  <td className="border px-3 py-3" style={{ borderColor: C.line, color: C.text }}>{fmt(row.iv)}</td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr>
                <td colSpan={12} className="border px-3 py-4" style={{ borderColor: C.line, color: C.dim }}>
                  No saved {title} rows for this interval.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SavedBigMoneyIntervals({ snapshots, intervalMinutes }) {
  const intervalRows = useMemo(
    () => buildBigMoneyIntervalRows(snapshots, intervalMinutes),
    [snapshots, intervalMinutes]
  );
  const latestInterval = useMemo(
    () => [...intervalRows].reverse().find(row => row.rows.length),
    [intervalRows]
  );
  const [expandedInterval, setExpandedInterval] = useState("");

  useEffect(() => {
    if (latestInterval) setExpandedInterval(latestInterval.label);
  }, [latestInterval]);

  return (
    <div className="grid gap-3">
      <div className="overflow-auto rounded border" style={{ borderColor: C.line }}>
        <table className="w-full min-w-[980px] border-collapse text-sm">
          <thead style={{ background: "#078574", color: C.gold }}>
            <tr>
              {["#", "Interval", "Rows", "CE", "PE", "Amount", "Still Active"].map(head => (
                <th key={head} className="border px-3 py-3 text-left" style={{ borderColor: C.line }}>{head}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {intervalRows.map(row => {
              const isExpanded = expandedInterval === row.label;
              return (
                <React.Fragment key={row.label}>
                  <tr
                    onClick={() => row.rows.length && setExpandedInterval(current => current === row.label ? "" : row.label)}
                    style={{
                      background: row.rows.length ? "rgba(20, 16, 16, 0.88)" : "rgba(255,255,255,0.015)",
                      color: row.rows.length ? C.gold : C.dim,
                      cursor: row.rows.length ? "pointer" : "default",
                    }}
                  >
                    <td className="border px-3 py-3" style={{ borderColor: C.line }}>{row.serial}</td>
                    <td className="border px-3 py-3" style={{ borderColor: C.line }}>{row.label}</td>
                    <td className="border px-3 py-3" style={{ borderColor: C.line }}>{row.rows.length || "--"}</td>
                    <td className="border px-3 py-3" style={{ borderColor: C.line }}>{row.ceRows.length || "--"}</td>
                    <td className="border px-3 py-3" style={{ borderColor: C.line }}>{row.peRows.length || "--"}</td>
                    <td className="border px-3 py-3" style={{ borderColor: C.line }}>{row.rows.length ? moneyCr(row.totalAmount) : "--"}</td>
                    <td className="border px-3 py-3" style={{ borderColor: C.line }}>{row.rows.length ? moneyCr(row.totalActive) : "--"}</td>
                  </tr>
                  {isExpanded && row.rows.length > 0 && (
                    <tr>
                      <td colSpan={7} className="border p-4" style={{ borderColor: C.line, background: "#050806" }}>
                        <div className="grid gap-5 xl:grid-cols-2">
                          <SavedPunchColumn title="CE" rows={row.ceRows} />
                          <SavedPunchColumn title="PE" rows={row.peRows} />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BigMoneyFlowTab({ tape, thresholdCr, onThresholdChange, history, selectedHistoryDate, onHistoryDateChange }) {
  const [innerTab, setInnerTab] = useState("options");
  const [searchText, setSearchText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState("remainingAmount");
  const [sortDirection, setSortDirection] = useState("desc");
  const tableRows = useMemo(() => {

      let rows =
          innerTab === "stocks"
              ? [...(tape.stocks || [])]
              : [...(tape.options || [])];

      if (searchText.trim()) {
          rows = rows.filter((r) =>
              (r.label || "")
                  .toLowerCase()
                  .includes(searchText.toLowerCase())
          );
      }

      rows.sort((a, b) => {

          const av = a[sortField];
          const bv = b[sortField];

          if (typeof av === "number" && typeof bv === "number") {
              return sortDirection === "asc"
                  ? av - bv
                  : bv - av;
          }

          return sortDirection === "asc"
              ? String(av ?? "").localeCompare(String(bv ?? ""))
              : String(bv ?? "").localeCompare(String(av ?? ""));

      });

      return rows;

  }, [
      innerTab,
      tape,
      searchText,
      sortField,
      sortDirection
  ]);

  const pagedRows = useMemo(() => {

      const start = (page - 1) * rowsPerPage;

      return tableRows.slice(
          start,
          start + rowsPerPage
      );

  }, [tableRows, page, rowsPerPage]);

  const totalPages = Math.max(
      1,
      Math.ceil(tableRows.length / rowsPerPage)
  );

  const dateOptions = useMemo(() => (history || []).map(record => record.date), [history]);
  const selectedHistory = useMemo(() => {
    return (history || []).find(record => record.date === selectedHistoryDate) || history?.[0] || null;
  }, [history, selectedHistoryDate]);
  const isSavedTab = innerTab === "1" || innerTab === "5";

  return (
    <div className="mt-5 grid gap-5">

      <div className="rounded border p-5" style={{ background: C.panel, borderColor: C.line }}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.28em]" style={{ color: C.dim }}>
              Big Player Single Punch Tracker
            </div>
            <h2 className="mt-2 text-2xl font-bold" style={{ color: C.gold }}>
              CE vs PE Large Flow
            </h2>
          </div>
          <label className="rounded border px-4 py-3" style={{ background: C.card, borderColor: C.line }}>
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: C.dim }}>Minimum Punch</div>
            <select
              value={thresholdCr}
              onChange={event => onThresholdChange(Number(event.target.value))}
              className="mt-2 rounded border bg-black px-3 py-2"
              style={{ borderColor: C.line, color: C.gold }}
            >
              <option value={5}>5Cr+</option>
              <option value={10}>10Cr+</option>
            </select>
          </label>
        </div>
        <div className="mt-4 text-sm leading-6" style={{ color: C.text }}>
          This tracks current NSE/BSE strike flow and fresh snapshot chunks whose traded value crosses the selected threshold.
          Side is inferred from live price/LTP movement because public NSE/BSE snapshots do not reveal the true order aggressor or player identity.
          Rows remain active until an opposite large chunk appears in the same stock or option strike.
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {[
          { key: "options", label: "CE / PE" },
          { key: "1", label: "1 Min" },
          { key: "5", label: "5 Min" },
        ].map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setInnerTab(tab.key)}
            className="rounded border px-4 py-2 text-sm font-bold"
            style={{
              borderColor: innerTab === tab.key ? C.gold : C.line,
              background: innerTab === tab.key ? "#2a2108" : C.card,
              color: innerTab === tab.key ? C.gold : C.dim,
            }}
          >
            {tab.label}
          </button>
        ))}
        {isSavedTab && (
          <select
            value={selectedHistory?.date || ""}
            onChange={event => onHistoryDateChange(event.target.value)}
            className="rounded border bg-black px-4 py-2 text-sm font-bold"
            style={{ borderColor: C.line, color: C.gold }}
          >
            {dateOptions.map(date => (
              <option key={date} value={date}>{date}</option>
            ))}
            {!dateOptions.length && <option value="">No saved dates</option>}
          </select>
        )}
        <button
          type="button"
          onClick={() => setInnerTab("stocks")}
          className="rounded border px-4 py-2 text-sm font-bold"
          style={{
            borderColor: innerTab === "stocks" ? C.gold : C.line,
            background: innerTab === "stocks" ? "#2a2108" : C.card,
            color: innerTab === "stocks" ? C.gold : C.dim,
          }}
        >
          Stocks
        </button>
      </div>

      <div className="mb-3 flex items-center justify-between">

          <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search symbol..."
              className="rounded border px-3 py-2 w-72"
              style={{
                  background: "#111",
                  color: "#fff",
                  borderColor: "#333"
              }}
          />

          <div
              className="text-sm"
              style={{ color: "#aaa" }}
          >
              Total Records : {tableRows.length}
          </div>

      </div>

      <div
          className="flex items-center justify-between mb-3"
        >

            <div>

                <select
                    value={rowsPerPage}
                    onChange={(e) => {

                        setRowsPerPage(Number(e.target.value));
                        setPage(1);

                    }}
                    className="rounded border px-2 py-2"
                >
                    <option value={50}>50 Rows</option>
                    <option value={100}>100 Rows</option>
                    <option value={250}>250 Rows</option>
                    <option value={500}>500 Rows</option>
                </select>

            </div>

            <div
                className="flex items-center gap-2"
            >

                <button
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                >
                    ◀
                </button>

                <span>

                    Page {page} / {totalPages}

                </span>

                <button
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                >
                    ▶
                </button>

            </div>

        </div>

      {innerTab === "options" && (

          <BigMoneyTable
              title="Big Money Flow"
              rows={pagedRows}
              type="options"
              sortField={sortField}
              sortDirection={sortDirection}
              setSortField={setSortField}
              setSortDirection={setSortDirection}
          />

      )}

      {innerTab === "stocks" && (

          <BigMoneyTable
              title="Stocks Big Money"
              rows={pagedRows}
              type="stocks"
              sortField={sortField}
              sortDirection={sortDirection}
              setSortField={setSortField}
              setSortDirection={setSortDirection}
          />

      )}

      {isSavedTab && (
        <SavedBigMoneyIntervals
          snapshots={selectedHistory?.minuteSnapshots || []}
          intervalMinutes={innerTab === "5" ? 5 : 1}
        />
      )}
    </div>
  );
}

export default function Indicators() {
  const [selectedIndex, setSelectedIndex] = useState("nifty");
  const [timeframe, setTimeframe] = useState(15);
  const [activeTab, setActiveTab] = useState("live");
  const [store, setStore] = useState(readStore);
  const [bigMoneyStore, setBigMoneyStore] = useState(readBigMoneyStore);
  const [bigMoneyThresholdCr, setBigMoneyThresholdCr] = useState(DEFAULT_BIG_MONEY_CR);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [controls, setControls] = useState(null);
  const [controlling, setControlling] = useState(false);
  const controlsRef = useRef(null);
  const controlVersions = useRef({ live: 0, bigMoney: 0 });
  const [error, setError] = useState("");
  const [bigMoneyHistory, setBigMoneyHistory] = useState([]);
  const [selectedBigMoneyDate, setSelectedBigMoneyDate] = useState("");
  const lastBigMoneySavedMinute = useRef("");

  const storageId = `${selectedIndex}_${timeframe}`;
  const candles = useMemo(() => store[storageId] || [], [store, storageId]);
  const selectedMeta = INDICES.find(item => item.key === selectedIndex) || INDICES[0];
  const indicators = useMemo(() => calculateIndicators(candles), [candles]);
  const signal = useMemo(() => getSignal(indicators), [indicators]);
  const latestCandle = candles[candles.length - 1];
  const latestIndex = snapshot?.indices?.find(item => item.key === selectedIndex);

  const loadBigMoneyHistory = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/bloomberg/big-money-history?t=${Date.now()}`, { cache: "no-store" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || "Big Money history fetch failed");
      setBigMoneyHistory(data.history || []);
      if (!selectedBigMoneyDate && data.history?.[0]?.date) {
        setSelectedBigMoneyDate(data.history[0].date);
      }
    } catch {
      setBigMoneyHistory([]);
    }
  };

  const saveBigMoneyHistorySnapshot = async nextSnapshot => {
    if (!controlsRef.current?.bigMoney) return;
    const options = bigMoneyHistoryRowsFromSnapshot(nextSnapshot);
    if (!options.length) return;

    const capturedAt = nextSnapshot.updatedAt || new Date().toISOString();
    const minuteKey = new Date(capturedAt).toISOString().slice(0, 16);
    if (lastBigMoneySavedMinute.current === minuteKey) return;
    lastBigMoneySavedMinute.current = minuteKey;

    try {
      const res = await fetch(`${API_BASE}/api/bloomberg/big-money-history`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rows: options,
          capturedAt,
          thresholdCr: bigMoneyThresholdCr,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || "Big Money history save failed");
      setBigMoneyHistory(data.history || []);
      if (!selectedBigMoneyDate && data.history?.[0]?.date) {
        setSelectedBigMoneyDate(data.history[0].date);
      }
    } catch {
      lastBigMoneySavedMinute.current = "";
    }
  };

  const updateCandles = (nextSnapshot, version) => {
    const nextIndex = nextSnapshot?.indices?.find(item => item.key === selectedIndex);
    const price = Number(nextIndex?.spot || 0);
    if (!price) return;

    const time = bucketTime(nextSnapshot.updatedAt || new Date().toISOString(), Number(timeframe));

    setStore(prev => {
      if (!controlsRef.current?.live || version !== controlVersions.current.live) return prev;
      const key = `${selectedIndex}_${timeframe}`;
      const existing = prev[key] || [];
      const last = existing[existing.length - 1];
      let nextCandles;

      if (last?.time === time) {
        nextCandles = [
          ...existing.slice(0, -1),
          {
            ...last,
            high: Math.max(last.high, price),
            low: Math.min(last.low, price),
            close: price,
            source: nextIndex.source || nextSnapshot.source || "NSE/BSE",
          },
        ];
      } else {
        nextCandles = [
          ...existing,
          {
            time,
            open: price,
            high: price,
            low: price,
            close: price,
            source: nextIndex.source || nextSnapshot.source || "NSE/BSE",
          },
        ].slice(-MAX_CANDLES);
      }

      const nextStore = { ...prev, [key]: nextCandles };
      saveStore(nextStore);
      return nextStore;
    });
  };

  const updateBigMoneyTape = (nextSnapshot, version) => {
    setBigMoneyStore(prev => {
      if (!controlsRef.current?.bigMoney || version !== controlVersions.current.bigMoney) return prev;
      const nextStore = updateBigMoneyPunchTape(nextSnapshot, prev, bigMoneyThresholdCr);
      saveBigMoneyStore(nextStore);
      saveBigMoneyHistorySnapshot(nextSnapshot);
      return nextStore;
    });
  };

  const changeBigMoneyThreshold = value => {
    setBigMoneyThresholdCr(value);
    if (!controlsRef.current?.bigMoney) return;
    lastBigMoneySavedMinute.current = "";
    const resetStore = {
      thresholdCr: value,
      updatedAt: new Date().toISOString(),
      previousOptionRows: [],
      previousStockRows: [],
      options: [],
      stocks: [],
      lastOptionEvents: [],
      lastStockEvents: [],
    };
    setBigMoneyStore(resetStore);
    saveBigMoneyStore(resetStore);
  };

  const loadSnapshot = async () => {
    const running = controlsRef.current;
    if (!running?.live && !running?.bigMoney) return;
    const versions = { ...controlVersions.current };
    const tab = running.live ? "live" : "bigMoney";
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/api/bloomberg/snapshot?indicatorTab=${tab}&t=${Date.now()}`, { cache: "no-store" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || "Indicator data fetch failed");
      if (data.collectionEnabled === false) return;
      if (controlsRef.current?.live && versions.live === controlVersions.current.live) {
        setSnapshot(data);
        updateCandles(data, versions.live);
      }
      if (controlsRef.current?.bigMoney && versions.bigMoney === controlVersions.current.bigMoney) {
        updateBigMoneyTape(data, versions.bigMoney);
      }
    } catch (err) {
      setError(err.message || "Indicator data fetch failed");
    } finally {
      setLoading(false);
    }
  };

  const setCollectionEnabled = async enabled => {
    const tab = activeTab;
    controlVersions.current[tab] += 1;
    setControlling(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/bloomberg/indicator-control`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tab, enabled }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Could not update collection");
      controlsRef.current = data.controls;
      setControls(data.controls);
    } catch (err) {
      setError(err.message);
    } finally {
      setControlling(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadControls = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/bloomberg/indicator-control`, { cache: "no-store" });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || "Could not load collection state");
        if (!cancelled) {
          controlsRef.current = data.controls;
          setControls(data.controls);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    };
    loadControls();
    loadBigMoneyHistory();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!controls?.live && !controls?.bigMoney) return undefined;
    loadSnapshot();
    const timer = setInterval(loadSnapshot, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controls?.live, controls?.bigMoney, selectedIndex, timeframe]);

  const e20 = latestValue(indicators.ema20);
  const e50 = latestValue(indicators.ema50);
  const pdi = latestValue(indicators.plusDi);
  const mdi = latestValue(indicators.minusDi);
  const adx = latestValue(indicators.adx);
  const crossText = e20 == null || e50 == null
    ? "Waiting for EMA 20/50"
    : e20 > e50
      ? `EMA 20 above EMA 50 on ${timeframe} min`
      : `EMA 20 below EMA 50 on ${timeframe} min`;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, padding: 26 }}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.42em]" style={{ color: C.dim }}>
            Nexovate Indicator Terminal
          </div>
          <h1 className="mt-2 text-3xl font-bold" style={{ color: C.gold }}>
            Indicators
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setCollectionEnabled(true)}
            disabled={!controls || controlling || controls[activeTab]}
            className="rounded border px-4 py-2 text-sm font-bold"
            style={{
              borderColor: controls?.[activeTab] ? C.green : C.line,
              background: controls?.[activeTab] ? "#063c2c" : C.card,
              color: controls?.[activeTab] ? "#d1fae5" : C.gold,
            }}
          >
            {controls?.[activeTab] ? "Started · 5s" : "Start"}
          </button>
          <button
            type="button"
            onClick={() => setCollectionEnabled(false)}
            disabled={!controls || controlling || !controls[activeTab]}
            className="rounded border px-4 py-2 text-sm font-bold"
            style={{ borderColor: C.line, background: C.card, color: C.gold }}
          >
            {controls && !controls[activeTab] ? "Stopped" : "Stop"}
          </button>
          <button
            type="button"
            onClick={loadSnapshot}
            disabled={loading || controlling || !controls?.[activeTab]}
            className="rounded border px-4 py-2 text-sm font-bold"
            style={{ borderColor: C.line, background: C.card, color: C.gold }}
          >
            <RefreshCw size={15} className="mr-2 inline" />
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        {INTERNAL_TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className="rounded border px-4 py-2 text-sm font-bold"
            style={{
              borderColor: activeTab === tab.key ? C.gold : C.line,
              background: activeTab === tab.key ? "#2a2108" : C.card,
              color: activeTab === tab.key ? C.gold : C.dim,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "live" ? (
        <>
      <div className="mt-6 grid gap-4 md:grid-cols-4">
        <label className="block rounded border p-3" style={{ background: C.panel, borderColor: C.line }}>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: C.dim }}>Index</div>
          <select
            value={selectedIndex}
            onChange={event => setSelectedIndex(event.target.value)}
            className="mt-2 w-full rounded border bg-black px-3 py-2"
            style={{ borderColor: C.line, color: C.gold }}
          >
            {INDICES.map(item => (
              <option key={item.key} value={item.key}>{item.label} - {item.exchange}</option>
            ))}
          </select>
        </label>

        <label className="block rounded border p-3" style={{ background: C.panel, borderColor: C.line }}>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: C.dim }}>Timeframe</div>
          <select
            value={timeframe}
            onChange={event => setTimeframe(Number(event.target.value))}
            className="mt-2 w-full rounded border bg-black px-3 py-2"
            style={{ borderColor: C.line, color: C.gold }}
          >
            {TIMEFRAMES.map(item => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </label>

        <Metric label="Live Spot" value={fmt(latestIndex?.spot || latestCandle?.close)} color={C.gold} />
        <Metric label="Source" value={latestIndex?.source || latestCandle?.source || selectedMeta.exchange} color={C.blue} />
      </div>

      {error && (
        <div className="mt-4 rounded border px-4 py-3 text-sm" style={{ borderColor: "rgba(239,68,68,0.5)", color: C.red }}>
          {error}
        </div>
      )}

      <div className="mt-5 grid gap-4 md:grid-cols-5">
        <Metric label="EMA 20" value={fmt(e20)} color={C.green} />
        <Metric label="EMA 50" value={fmt(e50)} color={C.red} />
        <Metric label="DI+" value={fmt(pdi)} color={C.green} />
        <Metric label="DI-" value={fmt(mdi)} color={C.red} />
        <Metric label="ADX 14" value={fmt(adx)} color={adx != null && adx >= 25 ? C.gold : C.dim} />
      </div>

      <div className="mt-5 rounded border p-4" style={{ background: C.panel, borderColor: signal.color }}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {signal.color === C.green ? <TrendingUp size={24} /> : signal.color === C.red ? <TrendingDown size={24} /> : <Activity size={24} />}
            <div>
              <div className="text-2xl font-bold" style={{ color: signal.color }}>{signal.label}</div>
              <div className="mt-1 text-sm" style={{ color: C.text }}>{signal.detail}</div>
            </div>
          </div>
          <div className="rounded border px-4 py-2 text-sm font-bold" style={{ borderColor: C.line, color: C.gold }}>
            {crossText}
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-[1.4fr_0.9fr]">
        <div className="rounded border p-4" style={{ background: C.panel, borderColor: C.line }}>
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.22em]" style={{ color: C.gold }}>
              <BarChart3 size={18} />
              Price With EMA Lines
            </div>
            <div className="text-xs" style={{ color: C.dim }}>{candles.length}/{MAX_CANDLES} candles</div>
          </div>
          <PriceChart candles={candles} indicators={indicators} />
        </div>

        <div className="rounded border p-4" style={{ background: C.panel, borderColor: C.line }}>
          <div className="mb-3 text-sm font-bold uppercase tracking-[0.22em]" style={{ color: C.gold }}>
            ADX / Directional Movement
          </div>
          <AdxChart indicators={indicators} />
          <div className="mt-4 grid gap-3 text-sm">
            <div style={{ color: C.dim }}>ADX below 20: weak trend</div>
            <div style={{ color: C.gold }}>ADX above 25: trend valid</div>
            <div style={{ color: C.green }}>DI+ above DI-: bullish direction</div>
            <div style={{ color: C.red }}>DI- above DI+: bearish direction</div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="mt-4 text-sm" style={{ color: C.dim }}>
          Fetching NSE/BSE snapshot...
        </div>
      )}
        </>
      ) : (
        <BigMoneyFlowTab
          tape={bigMoneyStore}
          thresholdCr={bigMoneyThresholdCr}
          onThresholdChange={changeBigMoneyThreshold}
          history={bigMoneyHistory}
          selectedHistoryDate={selectedBigMoneyDate}
          onHistoryDateChange={setSelectedBigMoneyDate}
        />
      )}
    </div>
  );
}
